import { useEffect, useState, type FunctionComponent } from "react";
import { z } from "zod";
import { useSearchParams } from "react-router-dom";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
} from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import Spinner from "../../../../components/spinner.components";
import { Input } from "../../../../components/ui/input";

import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import { useEphemeralSendOtpCommonHttp } from "../../../../hooks/http/common/users/ephemeral-send-otp.common.http";
import OTPPhoneEphemeral from "../../../../components/otp-phone-ephemeral.components";
import { useAuthenticationStore } from "../../../../store/authentication.store";

const FormSchema = z.object({
	phone: z.string().max(10),
});

export const LoginBrandAuthCommonPartial: FunctionComponent = () => {
	const [otpSent, setOtpSent] = useState<boolean>(false);

	const { setAuthenticationState } = useAuthenticationStore();

	const [searchParams] = useSearchParams();
	const campaign = searchParams.get("campaign");

	const { execute: sendOtpHttp, fetching: sendingOtp } =
		useEphemeralSendOtpCommonHttp();

	const { toast } = useToast();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			phone: "",
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.formState.errors) {
			const key = Object.keys(form.formState.errors)[0];
			if (key) {
				toast({
					content:
						form.formState.errors[key as keyof typeof form.formState.errors]
							?.message || "",
					toastType: "error",
				});
			}
		}
	}, [form.formState.errors]);

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		console.log(data.phone, campaign);
		const response = await sendOtpHttp({
			body: {
				identifierType: "phone",
				identifier: `+91${data.phone}:${campaign}`,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setOtpSent(true);
		setAuthenticationState({
			phone: data.phone,
			otp: response.data,
		});
	}

	if (otpSent) {
		return (
			<div className="flex justify-center items-center">
				<OTPPhoneEphemeral />
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center gap-12 w-full">
			<h1 className="w-full font-semibold text-3xl">Welcome back!</h1>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-8 w-full"
				>
					<FormField
						control={form.control}
						name="phone"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="font-semibold">
									Enter your mobile number
								</FormLabel>
								<FormControl className="relative">
									<div className="flex items-center">
										<Input
											type="number"
											typeof="number"
											maxLength={10}
											minLength={10}
											className="py-6 pl-24 h-16 text-lg placeholder:text-secondary tracking-wider"
											placeholder=""
											{...field}
										/>
										<div className="left-0 absolute ml-5 font-semibold text-lg tracking-wider cursor-pointer">
											(IN) +91
										</div>
									</div>
								</FormControl>
							</FormItem>
						)}
					/>

					<Button
						className="bg-primarybuttonbg py-8 w-full font-semibold text-black"
						type="submit"
					>
						{sendingOtp ? <Spinner width={20} /> : "Get OTP"}
					</Button>
				</form>
			</Form>
		</div>
	);
};
